import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { notificationsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, CheckCheck, ArrowRight, AtSign, MessageSquare, Reply } from 'lucide-react';
import { formatDateTime } from '../lib/utils';

// Get notification icon based on template_key
const getNotificationIcon = (templateKey) => {
  if (templateKey?.includes('mention')) {
    return <AtSign className="w-5 h-5" />;
  }
  if (templateKey?.includes('reply') || templateKey?.includes('thread')) {
    return <Reply className="w-5 h-5" />;
  }
  if (templateKey?.includes('comment')) {
    return <MessageSquare className="w-5 h-5" />;
  }
  return <Bell className="w-5 h-5" />;
};

// Get notification type label
const getNotificationLabel = (templateKey) => {
  if (templateKey === 'comment_mention') return 'Mentioned';
  if (templateKey === 'comment_group_mention') return 'Group Mention';
  if (templateKey === 'comment_thread_reply') return 'Thread Reply';
  if (templateKey === 'comment_reply') return 'Reply';
  return null;
};

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await notificationsApi.getAll();
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationsApi.markRead(id);
      // Update local state immediately instead of refetching
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read first if unread
    if (!notif.is_read) {
      await handleMarkRead(notif.id);
    }
    // Then navigate
    if (notif.request_id) {
      let url = `/requests/${notif.request_id}`;
      if (notif.comment_id) {
        url += `?tab=discussion&comment=${notif.comment_id}`;
        if (notif.template_key === 'doc_review_nudge') url += '&highlight=nudge';
      }
      navigate(url);
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <DashboardLayout title="Notifications">
      <div className="space-y-6" data-testid="notifications-page">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 font-['Manrope']">Notifications</h2>
            <p className="text-slate-500 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
            </p>
          </div>
          {unreadCount > 0 && (
            <Button variant="outline" onClick={handleMarkAllRead}>
              <CheckCheck size={16} className="mr-2" />
              Mark all as read
            </Button>
          )}
        </div>

        <Card>
          <CardContent className="p-0">
            {notifications.length === 0 ? (
              <div className="text-center py-16">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {notifications.map((notif) => {
                  const label = getNotificationLabel(notif.template_key);
                  return (
                    <div 
                      key={notif.id}
                      className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${!notif.is_read ? 'bg-blue-50/50' : ''}`}
                      onClick={() => handleNotificationClick(notif)}
                      data-testid={`notif-${notif.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          !notif.is_read 
                            ? notif.template_key?.includes('mention') 
                              ? 'bg-amber-100 text-amber-600' 
                              : 'bg-blue-100 text-blue-600'
                            : 'bg-slate-100 text-slate-400'
                        }`}>
                          {getNotificationIcon(notif.template_key)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            {label && (
                              <Badge className={`text-xs ${
                                notif.template_key?.includes('mention') 
                                  ? 'bg-amber-100 text-amber-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {label}
                              </Badge>
                            )}
                          </div>
                          <p className={`${!notif.is_read ? 'font-medium text-slate-900' : 'text-slate-700'}`}>
                            {notif.message}
                          </p>
                          {notif.preview && (
                            <div className="mt-2 text-sm text-slate-500 bg-slate-50 border-l-2 border-slate-300 px-3 py-2 rounded-r italic line-clamp-2">
                              &ldquo;{notif.preview}&rdquo;
                            </div>
                          )}
                          <p className="text-sm text-slate-400 mt-2">
                            {formatDateTime(notif.sent_at)}
                          </p>
                        </div>
                        {notif.request_id && (
                          <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
